import { Controller } from "@hotwired/stimulus"

/**
 * Polls a URL at a regular interval and replaces the element's innerHTML
 * when the server indicates completion. Stops polling once done.
 *
 * Usage:
 *   <div data-controller="poll-refresh"
 *        data-poll-refresh-url-value="/some/endpoint"
 *        data-poll-refresh-interval-value="3000">
 *     <span class="loading loading-dots"></span>
 *   </div>
 */
export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 4000 },
  }

  declare urlValue: string
  declare intervalValue: number

  private timer: ReturnType<typeof setInterval> | null = null

  connect() {
    this.startPolling()
  }

  disconnect() {
    this.stopPolling()
  }

  private startPolling() {
    this.timer = setInterval(() => this.poll(), this.intervalValue)
  }

  private stopPolling() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  private async poll() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "text/html" },
        credentials: "same-origin",
      })

      if (!response.ok) return

      const html = await response.text()
      if (html.trim()) {
        this.stopPolling()
        this.element.outerHTML = html
      }
    } catch {
      // Silently ignore network errors, will retry on next interval
    }
  }
}
